import React, { useEffect, useState } from "react";
import { usePost } from "../API";
import { MentionPerson } from "@aidkitorg/component-library";
import { safeParse } from "../Util";

export const useMentionsInNotes = () => {
  const getUsers = usePost("/user/list_names_and_emails");
  const [taggableUsers, setTaggableUsers] = useState<MentionPerson[]>([]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const usersResponse = await getUsers({});
      if (!isMounted || !Array.isArray(usersResponse)) {
        return;
      }

      const mentionsUsers = usersResponse
        .filter((user): user is MentionPerson => !!user?.name && !!user?.email)
        .map(({ name, email }) => ({ name, email }));

      setTaggableUsers(mentionsUsers);
    })();

    return () => {
      isMounted = false;
    };
  }, [getUsers]);

  return taggableUsers;
};

export const parseMentions = (metadata: unknown): MentionPerson[] => {
  let parsedMetadata = metadata;
  if (typeof metadata === 'string') {
    parsedMetadata = safeParse(metadata);
  }
  if (!parsedMetadata || typeof parsedMetadata !== 'object' || !('mentions' in parsedMetadata)) {
    return [];
  }

  const mentions = (parsedMetadata as { mentions?: unknown }).mentions;
  if (!Array.isArray(mentions)) return [];

  return mentions.filter((mention): mention is MentionPerson =>
    !!mention &&
    typeof mention === 'object' &&
    typeof (mention as { name?: unknown }).name === 'string' &&
    typeof (mention as { email?: unknown }).email === 'string'
  );
};

export const renderTextWithBoldMentions = (
  text: string,
  mentions: MentionPerson[],
  renderNonMentionText?: (text: string) => React.ReactNode
) => {
  if (!text) return text;
  if (mentions.length === 0) return renderNonMentionText ? renderNonMentionText(text) : text;

  const names = Array.from(new Set(mentions.map((mention) => mention.name.trim()).filter(Boolean)))
    .sort((a, b) => b.length - a.length)
    .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  if (names.length === 0) {
    return renderNonMentionText ? renderNonMentionText(text) : text;
  }

  const mentionRegex = new RegExp(`@(${names.join('|')})(?=$|[\\s.,!?;:])`, 'g');
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match = mentionRegex.exec(text);

  while (match) {
    const start = match.index;
    const mentionText = match[0];

    if (start > lastIndex) {
      const segment = text.slice(lastIndex, start);
      parts.push(
        <React.Fragment key={`text-${start}`}>
          {renderNonMentionText ? renderNonMentionText(segment) : segment}
        </React.Fragment>
      );
    }

    parts.push(<span className="font-bold" style={{ color: 'var(--ak-color-brand-blue-800)' }} key={`mention-${start}`}>{mentionText}</span>);
    lastIndex = start + mentionText.length;
    match = mentionRegex.exec(text);
  }

  if (lastIndex < text.length) {
    const segment = text.slice(lastIndex);
    parts.push(
      <React.Fragment key="text-end">
        {renderNonMentionText ? renderNonMentionText(segment) : segment}
      </React.Fragment>
    );
  }

  return <>{parts}</>;
};
